// LeftBar.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  type Variants,
  useAnimation,
} from "framer-motion";
import { Circle } from "lucide-react";

import MenuIcon from "../assets/icons/menu.svg";

import {
  Package,
  Boxes,
  LogOut,
  ShoppingCart,
  AlertTriangle,
  PauseCircle,
  Dot,
  Wrench,
  Building2,
  Ship,
  Users,
  FileText,
  ClipboardList,
  Bug,
  Plus,
  FileSpreadsheet,
  List,
  Zap,
  ChevronDown,
} from "lucide-react";

import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/auth-context";

type SubItem = {
  label: string;
  codigo?: string;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  themeColor?: string;
  children?: SubItem[];
};

type MainItem = {
  label: string;
  codigo?: string;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  themeColor?: string;
  subitems: SubItem[];
};

type Department = {
  name: string;
  items: MainItem[];
  slug?: string;
  routeKey?: string;
  ordem?: number | null;
  themeColor?: string;
  iconKey?: string;
};

type AddressOption = {
  value: string;
  label: string;
};

const LEFTBAR_CLASS = "eva-leftbar";
const DEFAULT_ADDRESS_VALUE = "delegado-waldir-guilherme-32";

const ADDRESS_OPTIONS: AddressOption[] = [
  {
    value: DEFAULT_ADDRESS_VALUE,
    label: "Delegado Waldir Guilherme, 32",
  },
];

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    marginTop: "56px",
    position: "fixed",
    top: 0,
    left: 0,
    width: "74px",
    height: "calc(100vh - 56px)",
    zIndex: 1200,
    boxSizing: "border-box",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    transition: "transform 0.3s ease-in-out",
    overflow: "visible",
    userSelect: "none",
  },
  scrollArea: {
    height: "100%",
    width: "100%",
    overflowY: "auto",
    overflowX: "visible",
    boxSizing: "border-box",
  },
  inner: {
    padding: "0px 0 18px",
    minHeight: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 0,
  },
  overlay: {
    position: "fixed",
    top: 56,
    left: 74,
    right: 0,
    height: "calc(100vh - 56px)",
    backgroundColor: "rgba(0, 0, 0, 0.18)",
    zIndex: 1150,
    pointerEvents: "none",
  },
  mobileHeader: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "60px",
    display: "flex",
    alignItems: "center",
    padding: "0 15px",
    justifyContent: "space-between",
    color: "#fff",
    zIndex: 1300,
  },
  titleMobile: {
    flex: 1,
    fontSize: "17px",
    fontWeight: 500,
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif",
    paddingRight: "30px",
  },
  migrationMarksWrapper: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    marginLeft: "auto",
  },
  migrationMark: {
    width: 3,
    height: 18,
    borderRadius: 999,
    background: "rgba(16,185,129,0.95)",
    boxShadow: "0 0 0 1px rgba(6,95,70,0.35)",
  },
  migrationMarkOrange: {
    width: 3,
    height: 18,
    borderRadius: 999,
    background: "rgba(249,115,22,0.95)",
    boxShadow: "0 0 0 1px rgba(154,52,18,0.35)",
  },
  migrationMarkBlue: {
    width: 3,
    height: 18,
    borderRadius: 999,
    background: "rgba(59,130,246,0.95)",
    boxShadow: "0 0 0 1px rgba(30,64,175,0.35)",
  },
};

const DevBadge: React.FC = () => (
  <span
    title="Ainda não finalizado"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginLeft: 6,
      borderRadius: 6,
      padding: "1px 4px",
      background: "rgba(255, 200, 0, 0.20)",
      border: "1px solid rgba(255, 200, 0, 0.52)",
    }}
  >
    <Wrench size={12} style={{ color: "#FFD34D" }} />
  </span>
);

const MAIN_THAT_GO_TO_FIRST_SUB = new Set(["saidas", "saídas"]);

const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const slugify = (s: string) =>
  norm(s)
    .replace(/&/g, "e")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();

const getDeptPermKey = (d: Department) =>
  d.slug ? String(d.slug) : slugify(d.name);

const getDeptRouteKey = (d: Department) =>
  d.routeKey ? String(d.routeKey) : d.slug ? String(d.slug) : slugify(d.name);

const cleanCodigoForRoute = (dept: Department, codigo?: string) => {
  const raw = String(codigo || "").trim();
  if (!raw) return "";

  const deptKey = slugify(getDeptRouteKey(dept));
  const parts = slugify(raw).split("-").filter(Boolean);

  if (!parts.length) return "";
  if (parts[0] === deptKey) parts.shift();
  if (parts[parts.length - 1] === deptKey) parts.pop();

  return parts.join("-");
};

const getMainSlug = (item: MainItem) => slugify(item.label);

const getSubSlug = (dept: Department, sub: SubItem) => {
  const cleaned = cleanCodigoForRoute(dept, sub.codigo);
  return cleaned ? cleaned : slugify(sub.label);
};

const getDeepestFirstVisibleSub = (
  dept: Department,
  subitems: SubItem[],
  visibleFn: (sub: SubItem) => boolean
): SubItem | null => {
  for (const sub of subitems) {
    if (!sub || !visibleFn(sub)) continue;
    const children = Array.isArray(sub.children) ? sub.children : [];
    if (children.length > 0) {
      const deeper = getDeepestFirstVisibleSub(dept, children, visibleFn);
      return deeper || sub;
    }
    return sub;
  }
  return null;
};

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [13, 60, 70];
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return [13, 60, 70];
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b))
    return [13, 60, 70];
  return [r, g, b];
}

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

const toRgba = (hex: string | undefined, alpha = 1) => {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const darkenHex = (hex: string | undefined, amount = 16) => {
  const [r, g, b] = hexToRgbComponents(hex);
  const nr = clamp(r - amount, 0, 255);
  const ng = clamp(g - amount, 0, 255);
  const nb = clamp(b - amount, 0, 255);
  return `rgb(${nr}, ${ng}, ${nb})`;
};

const DeptInternetIcon: React.FC<{
  iconKey?: string;
  size?: number;
  color?: string;
  alt?: string;
  style?: React.CSSProperties;
}> = ({ iconKey, size = 22, color = "#ffffff", alt = "", style }) => {
  const safeKey =
    typeof iconKey === "string" && iconKey.includes(":")
      ? iconKey
      : "lucide:circle";

  const src = `https://api.iconify.design/${safeKey}.svg?color=${encodeURIComponent(
    color
  )}`;

  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      draggable={false}
      loading="eager"
      style={{
        width: size,
        height: size,
        display: "block",
        flexShrink: 0,
        userSelect: "none",
        pointerEvents: "none",
        ...style,
      }}
      onError={(e) => {
        e.currentTarget.src =
          "https://api.iconify.design/lucide:circle.svg?color=%23ffffff";
      }}
    />
  );
};

const OptionIcon: React.FC<{
  label: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ label, size = 24, className, style }) => {
  const rawLower = label.trim().toLowerCase();
  const base = norm(label).replace(/\s+/g, "-").split("-")[0];
  const common = { className, style };

  if (rawLower === "colaboradores") return <Users size={size} {...common} />;
  if (base === "circle-outline")
    return <Circle size={14} {...common} style={{ ...style, opacity: 0.9 }} />;
  if (
    rawLower === "d. empresa" ||
    rawLower === "documentacao da empresa" ||
    rawLower === "documentação da empresa"
  )
    return <FileText size={size} {...common} />;
  if (
    rawLower === "solicitações" ||
    rawLower === "solicitacoes" ||
    rawLower === "minhas solicitações" ||
    rawLower === "minhas solicitacoes"
  )
    return <ClipboardList size={size} {...common} />;
  if (rawLower === "problemas") return <Bug size={size} {...common} />;
  if (base === "produtos") return <Package size={size} {...common} />;
  if (base === "estoque") return <Boxes size={size} {...common} />;
  if (base === "saidas" || base === "saídas")
    return <LogOut size={size} {...common} />;
  if (base === "vendas") return <ShoppingCart size={size} {...common} />;
  if (base === "perdas") return <AlertTriangle size={size} {...common} />;
  if (base === "standby") return <PauseCircle size={size} {...common} />;
  if (base === "empresas") return <Building2 size={size} {...common} />;
  if (base === "embarcacoes") return <Ship size={size} {...common} />;
  if (base === "circle") return <Dot size={14} {...common} />;

  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        ...style,
      }}
    />
  );
};

const MiniActionIcon: React.FC<{ label: string }> = ({ label }) => {
  const n = norm(label);

  if (n.includes("incluir") || n.includes("registrar")) {
    return <Plus size={12} />;
  }
  if (n.includes("planilha")) {
    return <FileSpreadsheet size={12} />;
  }
  if (n.includes("exibir") || n.includes("listar")) {
    return <List size={12} />;
  }
  if (n.includes("faturar") || n.includes("etapas")) {
    return <Zap size={12} />;
  }

  return <Dot size={12} />;
};

const sidebarVariants: Variants = {
  hidden: { x: "-100%", opacity: 0 },
  show: {
    x: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 260, damping: 26 },
  },
};

const flyoutVariants: Variants = {
  hidden: { opacity: 0, y: -4 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.14 },
  },
  exit: {
    opacity: 0,
    y: -2,
    transition: { duration: 0.12 },
  },
};

const mobileDeptVariants: Variants = {
  collapsed: { height: 0, opacity: 0, transition: { duration: 0.18 } },
  open: {
    height: "auto",
    opacity: 1,
    transition: { type: "spring", stiffness: 250, damping: 26 },
  },
};

type FlyoutColumn =
  | { type: "main_list"; items: MainItem[] }
  | { type: "single"; item: MainItem }
  | { type: "standby_group"; items: MainItem[] };

const LeftBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openDepts, setOpenDepts] = useState<string[]>([]);
  const [collapsedByUser, setCollapsedByUser] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [hoveredDeptName, setHoveredDeptName] = useState<string | null>(null);
  const [hoveredRailDeptName, setHoveredRailDeptName] = useState<string | null>(
    null
  );
  const [selectedAddressByDept, setSelectedAddressByDept] = useState<
    Record<string, string>
  >({});

  const closeFlyoutTimer = useRef<number | null>(null);
  const { theme, systemTitle, setDepartmentColor } = useTheme();
  const { sessao } = useAuth();

  useReducedMotion();
  const sidebarControls = useAnimation();

  const roles = sessao?.roles || [];
  const hasRoleSolicitante = roles.includes("solicitante");

  const hasVisibleChildren = (sub: SubItem) =>
    Array.isArray(sub.children) && sub.children.some(subItemVisible);

  const getSubItemColor = (
    deptColor: string,
    level: number,
    selected: boolean
  ): string => {
    if (selected) return darkenHex(deptColor, 6);
    if (level === 0) return toRgba(deptColor, 0.92);
    return "#8B93A8";
  };

  const getSubItemHoverColor = (deptColor: string, level: number): string => {
    if (level === 0) return darkenHex(deptColor, 4);
    return "#6F778B";
  };

  const userDept = (sessao?.departamento || "").toLowerCase();
  const isDev = userDept === "desenvolvedor";

  const primaryColor = theme?.primary || "#0D3C46";
  const menuButtonsSelection = theme?.menu_buttons_selection || "#19D4CF";

  const flyoutBg = "#F7F8F9";
  const flyoutBorder = "rgba(25, 45, 60, 0.08)";
  const iconIdle = "rgba(255,255,255,0.92)";
  const iconMuted = "rgba(255,255,255,0.60)";
  const iconActive = "#ffffff";

  const COLUMN_WIDTH = 320;
  const COLUMN_GAP = 16;
  const FLYOUT_PADDING_X = 22;
  const FLYOUT_PADDING_Y = 14;

  const solicitanteDept: Department = useMemo(
    () => ({
      name: "Minhas Solicitações",
      slug: "minhas-solicitacoes",
      routeKey: "minhas-solicitacoes",
      ordem: -100,
      themeColor: "#0a343d",
      iconKey: "lucide:clipboard-list",
      items: [
        {
          label: "Laboratório - Cases",
          devOnly: false,
          developed: true,
          migrated: false,
          themeColor: "#0a343d",
          subitems: [],
        },
      ],
    }),
    []
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1000;
      setIsMobile(mobile);
      setIsOpen(!mobile);
      sidebarControls.start(mobile ? "hidden" : "show");
      if (mobile) setHoveredDeptName(null);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarControls]);

  const mapSubItemRecursive = (sub: any, fallbackColor?: string): SubItem => ({
    label: String(sub?.label || ""),
    codigo: String(sub?.codigo || ""),
    devOnly: sub?.devOnly === true || sub?.dev_only === true,
    developed: sub?.developed === true,
    migrated: sub?.migrated === true,
    markOrange: sub?.markOrange === true || sub?.mark_orange === true,
    markBlue: sub?.markBlue === true || sub?.mark_blue === true,
    themeColor:
      sub?.themeColor !== undefined && sub?.themeColor !== null
        ? String(sub.themeColor)
        : fallbackColor ?? "#0a343d",
    children: Array.isArray(sub?.children)
      ? sub.children.map((child: any) =>
          mapSubItemRecursive(child, fallbackColor)
        )
      : [],
  });

  const baseDepartments: Department[] = useMemo(() => {
    const rawDepartments = Array.isArray(sessao?.departamentos_acessiveis)
      ? sessao?.departamentos_acessiveis ?? []
      : [];

    return rawDepartments.map((dept: any, index: number) => ({
      name: String(dept?.nome || ""),
      slug: String(dept?.slug || ""),
      routeKey:
        dept?.routeKey !== undefined && dept?.routeKey !== null
          ? String(dept.routeKey)
          : dept?.route_key !== undefined && dept?.route_key !== null
          ? String(dept.route_key)
          : String(dept?.slug || ""),
      ordem:
        dept?.ordem !== undefined && dept?.ordem !== null
          ? Number(dept.ordem)
          : index,
      themeColor:
        dept?.themeColor !== undefined && dept?.themeColor !== null
          ? String(dept.themeColor)
          : dept?.theme_color !== undefined && dept?.theme_color !== null
          ? String(dept.theme_color)
          : "#0a343d",
      iconKey:
        dept?.iconKey !== undefined && dept?.iconKey !== null
          ? String(dept.iconKey)
          : dept?.icon_key !== undefined && dept?.icon_key !== null
          ? String(dept.icon_key)
          : "lucide:circle",
      items: Array.isArray(dept?.modulos)
        ? dept.modulos.map((mod: any) => ({
            label: String(mod?.label || ""),
            codigo: String(mod?.codigo || ""),
            devOnly: mod?.devOnly === true || mod?.dev_only === true,
            developed: mod?.developed === true,
            migrated: mod?.migrated === true,
            markOrange: mod?.markOrange === true || mod?.mark_orange === true,
            markBlue: mod?.markBlue === true || mod?.mark_blue === true,
            themeColor:
              mod?.themeColor !== undefined && mod?.themeColor !== null
                ? String(mod.themeColor)
                : dept?.themeColor ?? "#0a343d",
            subitems: Array.isArray(mod?.subitems)
              ? mod.subitems.map((sub: any) =>
                  mapSubItemRecursive(
                    sub,
                    mod?.themeColor ?? dept?.themeColor ?? "#0a343d"
                  )
                )
              : [],
          }))
        : [],
    }));
  }, [sessao?.departamentos_acessiveis]);

  const departmentsSource: Department[] = useMemo(() => {
    if (!hasRoleSolicitante) return baseDepartments;

    const already = baseDepartments.some(
      (d) => norm(getDeptRouteKey(d)) === norm(getDeptRouteKey(solicitanteDept))
    );
    if (already) return baseDepartments;

    return [solicitanteDept, ...baseDepartments];
  }, [hasRoleSolicitante, baseDepartments, solicitanteDept]);

  const canSeeDept = useMemo(() => {
    const accessibleKeys = new Set(
      departmentsSource.map((dept) => norm(getDeptPermKey(dept)))
    );

    return (dept: Department) => {
      const permKey = norm(getDeptPermKey(dept));
      const routeKey = norm(getDeptRouteKey(dept));

      if (hasRoleSolicitante && permKey === "minhas-solicitacoes") return true;

      return accessibleKeys.has(permKey) || accessibleKeys.has(routeKey);
    };
  }, [departmentsSource, hasRoleSolicitante]);

  const itemVisible = (item: MainItem) => (isDev ? true : !item.devOnly);
  const subItemVisible = (sub: SubItem) => (isDev ? true : !sub.devOnly);

  const renderLabelWithBadge = (
    label: string,
    developed?: boolean,
    migrated?: boolean,
    markOrange?: boolean,
    markBlue?: boolean
  ) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
        minWidth: 0,
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>

      {isDev && developed === false ? <DevBadge /> : null}

      {isDev && (migrated || markOrange || markBlue) && (
        <span style={styles.migrationMarksWrapper}>
          {migrated && <span style={styles.migrationMark} />}
          {markOrange && <span style={styles.migrationMarkOrange} />}
          {markBlue && <span style={styles.migrationMarkBlue} />}
        </span>
      )}
    </span>
  );

  const getDepartmentAddressKey = (dept: Department) =>
    `${getDeptRouteKey(dept)}::address`;

  const getSelectedAddressValue = (dept: Department) =>
    selectedAddressByDept[getDepartmentAddressKey(dept)] ||
    DEFAULT_ADDRESS_VALUE;

  const handleAddressChange = (dept: Department, value: string) => {
    const deptKey = getDepartmentAddressKey(dept);

    setSelectedAddressByDept((prev) => ({
      ...prev,
      [deptKey]: value,
    }));
  };

  const renderAddressSelector = (dept: Department) => {
    const value = getSelectedAddressValue(dept);

    return (
      <div
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          minWidth: 0,
          maxWidth: 290,
          color: "#8F96A3",
          fontSize: 13,
          fontWeight: 400,
          lineHeight: 1.2,
        }}
      >
        <select
          value={value}
          onChange={(e) => handleAddressChange(dept, e.target.value)}
          aria-label={`Selecionar endereço de ${dept.name}`}
          style={{
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            border: "none",
            outline: "none",
            background: "transparent",
            boxShadow: "none",
            color: "#000000",
            fontSize: 14,
            fontWeight: 500,
            padding: "0 18px 0 0",
            margin: 0,
            cursor: "pointer",
            minWidth: 0,
            maxWidth: 290,
            width: "100%",
            textAlign: "right",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {ADDRESS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <ChevronDown
          size={14}
          strokeWidth={2}
          style={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            color: "#9AA1AC",
          }}
        />
      </div>
    );
  };

  const buildSubPath = (
    dept: Department,
    item: MainItem,
    sub: SubItem,
    ancestry: SubItem[] = []
  ): string => {
    const baseDeptPath = `/${getDeptRouteKey(dept)}`;
    const baseMainPath = `${baseDeptPath}/${getMainSlug(item)}`;
    const chain = [...ancestry, sub]
      .map((entry) => getSubSlug(dept, entry))
      .filter(Boolean);

    return `${baseMainPath}/${chain.join("/")}`;
  };

  const collectDeepestFirstPath = (
    dept: Department,
    item: MainItem,
    subitems: SubItem[]
  ): string | null => {
    for (const sub of subitems.filter(subItemVisible)) {
      const children = (sub.children || []).filter(subItemVisible);
      if (children.length > 0) {
        const deep = collectDeepestFirstPath(dept, item, children);
        if (deep) {
          return `${buildSubPath(dept, item, sub)}/${deep
            .split("/")
            .filter(Boolean)
            .slice(-1 * deep.split("/").filter(Boolean).length)
            .join("/")}`;
        }
      }
      return buildSubPath(dept, item, sub);
    }
    return null;
  };

  const getFirstVisibleNestedPath = (
    dept: Department,
    item: MainItem,
    subitems: SubItem[],
    ancestry: SubItem[] = []
  ): string | null => {
    for (const sub of subitems.filter(subItemVisible)) {
      const currentPath = buildSubPath(dept, item, sub, ancestry);
      const children = (sub.children || []).filter(subItemVisible);
      if (children.length > 0) {
        const deeper = getFirstVisibleNestedPath(dept, item, children, [
          ...ancestry,
          sub,
        ]);
        return deeper || currentPath;
      }
      return currentPath;
    }
    return null;
  };

  const buildFirstPath = (dept: Department) => {
    const baseDept = `/${getDeptRouteKey(dept)}`;
    const visibleItems = dept.items.filter(itemVisible);
    if (!visibleItems.length) return baseDept;

    const firstItem = visibleItems[0];
    const mainSlug = getMainSlug(firstItem);
    const baseMain = `${baseDept}/${mainSlug}`;

    const visibleSubs = (firstItem.subitems || []).filter(subItemVisible);
    const isGoFirstSub =
      MAIN_THAT_GO_TO_FIRST_SUB.has(norm(firstItem.label)) &&
      visibleSubs.length > 0;

    if (isGoFirstSub) {
      return (
        getFirstVisibleNestedPath(dept, firstItem, visibleSubs) || baseMain
      );
    }

    return baseMain;
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1000;
      setIsMobile(mobile);
      setIsOpen(!mobile);
      sidebarControls.start(mobile ? "hidden" : "show");
      if (mobile) setHoveredDeptName(null);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarControls]);

  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length === 0) return;

    const deptFromPath = parts[0];

    const dept =
      departmentsSource.find(
        (d) => norm(getDeptRouteKey(d)) === norm(deptFromPath)
      ) ||
      departmentsSource.find(
        (d) => norm(getDeptPermKey(d)) === norm(deptFromPath)
      );

    if (!dept) return;
    if (!canSeeDept(dept)) return;

    if (
      !openDepts.includes(dept.name) &&
      !collapsedByUser.includes(dept.name)
    ) {
      setOpenDepts((prev) => [...prev, dept.name]);
    }
  }, [
    location.pathname,
    departmentsSource,
    canSeeDept,
    openDepts,
    collapsedByUser,
    isMobile,
  ]);

  useEffect(() => {
    if (!sessao) return;
    if (!departmentsSource.length) return;
    if (isDev) return;

    const parts = location.pathname.split("/").filter(Boolean);

    if (parts.length > 0) {
      const currentDeptFromPath = parts[0];

      const existingDept = departmentsSource.find(
        (d) =>
          norm(getDeptRouteKey(d)) === norm(currentDeptFromPath) ||
          norm(getDeptPermKey(d)) === norm(currentDeptFromPath)
      );

      if (existingDept) {
        setOpenDepts((prev) =>
          prev.includes(existingDept.name) ? prev : [existingDept.name]
        );
        return;
      }
    }

    if (hasRoleSolicitante) {
      const solicitante = departmentsSource.find(
        (d) => norm(getDeptRouteKey(d)) === "minhas-solicitacoes"
      );

      if (solicitante) {
        setOpenDepts((prev) =>
          prev.includes(solicitante.name) ? prev : [solicitante.name]
        );
        navigate(buildFirstPath(solicitante), { replace: true });
        return;
      }
    }

    const preferredDept =
      departmentsSource.find(
        (d) =>
          norm(getDeptPermKey(d)) === norm(userDept) ||
          norm(getDeptRouteKey(d)) === norm(userDept)
      ) || departmentsSource[0];

    if (!preferredDept) return;

    setOpenDepts((prev) =>
      prev.includes(preferredDept.name) ? prev : [preferredDept.name]
    );

    navigate(buildFirstPath(preferredDept), { replace: true });
  }, [
    sessao,
    departmentsSource,
    isDev,
    hasRoleSolicitante,
    userDept,
    location.pathname,
    navigate,
  ]);

  const toggleDepartment = (dept: Department) => {
    if (!canSeeDept(dept)) return;

    setOpenDepts((prev) => {
      const isOpenDept = prev.includes(dept.name);
      if (isOpenDept) {
        setCollapsedByUser((c) =>
          c.includes(dept.name) ? c : [...c, dept.name]
        );
        return prev.filter((d) => d !== dept.name);
      } else {
        setCollapsedByUser((c) => c.filter((d) => d !== dept.name));
        return [...prev, dept.name];
      }
    });
  };

  const parts = location.pathname.split("/").filter(Boolean);
  const currentDeptFromPath = parts.length > 0 ? parts[0] : null;

  const visibleDepartments = useMemo(() => {
    return departmentsSource
      .filter((d) => canSeeDept(d))
      .sort((a, b) => Number(a.ordem ?? 9999) - Number(b.ordem ?? 9999));
  }, [departmentsSource, canSeeDept]);

  const isDeptSelected = (dept: Department) => {
    if (!currentDeptFromPath) return false;
    return (
      norm(currentDeptFromPath) === norm(getDeptRouteKey(dept)) ||
      norm(currentDeptFromPath) === norm(getDeptPermKey(dept))
    );
  };

  const hoveredDept = useMemo(() => {
    if (!hoveredDeptName) return null;
    return visibleDepartments.find((d) => d.name === hoveredDeptName) || null;
  }, [hoveredDeptName, visibleDepartments]);

  const activeDeptColor =
    hoveredDept?.themeColor ||
    visibleDepartments.find((d) => isDeptSelected(d))?.themeColor ||
    primaryColor;

  const dynamicFlyoutTitle = darkenHex(activeDeptColor, 8);
  const dynamicFlyoutText = darkenHex(activeDeptColor, 2);
  const dynamicFlyoutSub = toRgba(activeDeptColor, 0.9);
  const dynamicFlyoutChild = "#8B93A8";
  const dynamicFlyoutChildHover = "#727C93";

  const hoveredDeptItems = useMemo(() => {
    if (!hoveredDept) return [];
    return hoveredDept.items.filter(itemVisible);
  }, [hoveredDept, isDev]);

  useEffect(() => {
    const selectedDept = visibleDepartments.find((dept) =>
      isDeptSelected(dept)
    );

    if (selectedDept) {
      setDepartmentColor(selectedDept.themeColor || "#0a343d");
    } else {
      setDepartmentColor("#0a343d");
    }
  }, [visibleDepartments, currentDeptFromPath, setDepartmentColor]);

  const getMainPath = (dept: Department, item: MainItem) => {
    const baseDeptPath = `/${getDeptRouteKey(dept)}`;
    const mainSlug = getMainSlug(item);
    const baseMainPath = `${baseDeptPath}/${mainSlug}`;
    const visibleSubs = (item.subitems || []).filter(subItemVisible);

    if (
      MAIN_THAT_GO_TO_FIRST_SUB.has(norm(item.label)) &&
      visibleSubs.length > 0
    ) {
      return getFirstVisibleNestedPath(dept, item, visibleSubs) || baseMainPath;
    }

    return baseMainPath;
  };

  const isPathSelected = (
    dept: Department,
    item: MainItem,
    sub: SubItem,
    ancestry: SubItem[] = []
  ): boolean => {
    const currentPath = buildSubPath(dept, item, sub, ancestry);

    if (
      location.pathname === currentPath ||
      location.pathname.startsWith(`${currentPath}/`)
    ) {
      return true;
    }

    const children = (sub.children || []).filter(subItemVisible);
    return children.some((child) =>
      isPathSelected(dept, item, child, [...ancestry, sub])
    );
  };

  const scheduleCloseFlyout = () => {
    if (closeFlyoutTimer.current) {
      window.clearTimeout(closeFlyoutTimer.current);
    }
    closeFlyoutTimer.current = window.setTimeout(() => {
      setHoveredDeptName(null);
    }, 100);
  };

  const cancelCloseFlyout = () => {
    if (closeFlyoutTimer.current) {
      window.clearTimeout(closeFlyoutTimer.current);
      closeFlyoutTimer.current = null;
    }
  };

  const closeOverlay = () => {
    setHoveredDeptName(null);
  };

  const showBackdrop = !isMobile && !!hoveredDept;

  const groupedFlyoutItems = useMemo<FlyoutColumn[]>(() => {
    if (!hoveredDeptItems.length) return [];

    const standbyItems = hoveredDeptItems.filter((item) =>
      norm(item.label).startsWith("standby")
    );
    const normalItems = hoveredDeptItems.filter(
      (item) => !norm(item.label).startsWith("standby")
    );

    const groups: FlyoutColumn[] = [];
    groups.push({ type: "main_list", items: normalItems.slice(0, 3) });
    normalItems.slice(3).forEach((item) => {
      groups.push({ type: "single", item });
    });

    if (standbyItems.length) {
      groups.push({ type: "standby_group", items: standbyItems });
    }

    return groups.filter((g) => {
      if (g.type === "main_list") return g.items.length > 0;
      if (g.type === "standby_group") return g.items.length > 0;
      return true;
    });
  }, [hoveredDeptItems]);

  const renderNestedSubItems = (
    dept: Department,
    item: MainItem,
    subitems: SubItem[],
    level = 0,
    isMobileView = false,
    ancestry: SubItem[] = []
  ): React.ReactNode => {
    return subitems.filter(subItemVisible).map((sub, idx) => {
      const children = (sub.children || []).filter(subItemVisible);
      const subPath = buildSubPath(dept, item, sub, ancestry);
      const isSelected = isPathSelected(dept, item, sub, ancestry);
      const isChild = level > 0;

      const deptColor = dept.themeColor || activeDeptColor || "#0a343d";

      return (
        <div
          key={`${item.label}-${
            sub.codigo || sub.label
          }-${level}-${idx}-${ancestry
            .map((a) => a.codigo || a.label)
            .join("-")}`}
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: level === 0 ? 0 : 2,
          }}
        >
          <NavLink
            to={subPath}
            onClick={() => {
              if (isMobileView) setIsOpen(false);
              closeOverlay();
            }}
            className={`${LEFTBAR_CLASS}-sub-link`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              minHeight: isChild ? 28 : 32,
              padding: isChild ? "4px 10px 4px 10px" : "6px 10px",
              marginLeft: 0,
              marginBottom: children.length > 0 ? 2 : 4,
              borderRadius: 8,
              textDecoration: "none",
              background: isSelected
                ? toRgba(menuButtonsSelection, isChild ? 0.08 : 0.14)
                : "transparent",
              transition: "all 160ms ease",
              boxSizing: "border-box",
            }}
            onMouseEnter={(e) => {
              if (!isSelected) {
                e.currentTarget.style.background = isChild
                  ? "rgba(15,23,42,0.028)"
                  : "rgba(15,23,42,0.035)";
                e.currentTarget.style.transform = "translateX(2px)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected) {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.transform = "translateX(0)";
              }
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 12,
                minWidth: 12,
                color: isChild ? "#8B93A8" : toRgba(deptColor, 0.86),
                opacity: isChild ? 0.9 : 1,
                flexShrink: 0,
              }}
            >
              <MiniActionIcon label={sub.label} />
            </span>

            <span
              style={{
                display: "block",
                minWidth: 0,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: isChild
                  ? "#8B93A8"
                  : isSelected
                  ? darkenHex(deptColor, 6)
                  : toRgba(deptColor, 0.92),
                fontSize: isChild ? 12.6 : 13.5,
                fontWeight: isChild ? 400 : isSelected ? 600 : 500,
                lineHeight: 1.2,
              }}
            >
              {sub.label}
            </span>

            {isDev && sub.developed === false ? <DevBadge /> : null}
          </NavLink>

          {children.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                marginLeft: isMobileView ? 14 : 20,
                paddingLeft: 10,
                borderLeft: "1px solid rgba(120,130,150,0.18)",
              }}
            >
              {renderNestedSubItems(
                dept,
                item,
                children,
                level + 1,
                isMobileView,
                [...ancestry, sub]
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const renderSubTreeDesktop = (dept: Department, item: MainItem) => {
    const visibleSubs = (item.subitems || []).filter(subItemVisible);
    if (!visibleSubs.length) return null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          paddingLeft: 6,
          paddingRight: 4,
          paddingBottom: 2,
        }}
      >
        {renderNestedSubItems(dept, item, visibleSubs, 0, false, [])}
      </div>
    );
  };

  const renderSubTreeMobile = (dept: Department, item: MainItem) => {
    const visibleSubs = (item.subitems || []).filter(subItemVisible);
    if (!visibleSubs.length) return null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginBottom: 4,
        }}
      >
        {renderNestedSubItems(dept, item, visibleSubs, 0, true, [])}
      </div>
    );
  };

  return (
    <>
      <style>{`
        .${LEFTBAR_CLASS} {
          position: relative;
          overflow: visible;
          -webkit-font-smoothing: antialiased;
          text-rendering: geometricPrecision;
        }

        .${LEFTBAR_CLASS}-scroll {
          height: 100%;
          width: 100%;
          overflow-y: auto;
          overflow-x: visible;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.16) transparent;
        }

        .${LEFTBAR_CLASS}-scroll::-webkit-scrollbar {
          width: 5px;
        }

        .${LEFTBAR_CLASS}-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .${LEFTBAR_CLASS}-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(255,255,255,0.16);
          border-radius: 999px;
          border: 1px solid transparent;
          background-clip: padding-box;
        }

        .${LEFTBAR_CLASS}-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255,255,255,0.26);
        }

        .${LEFTBAR_CLASS}-rail-button {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 56px;
          border-radius: 0;
          border: none;
          background: transparent;
          color: ${iconMuted};
          cursor: pointer;
          transition:
            background-color 180ms ease,
            color 180ms ease;
          text-decoration: none;
          overflow: hidden;
          padding: 0;
        }

        .${LEFTBAR_CLASS}-rail-button:hover {
          color: ${iconIdle};
        }

        .${LEFTBAR_CLASS}-rail-button.is-active {
          color: ${iconActive};
        }

        .${LEFTBAR_CLASS}-main-link,
        .${LEFTBAR_CLASS}-sub-link,
        .${LEFTBAR_CLASS}-list-button {
          text-decoration: none;
          color: rgba(90, 98, 110, 0.95);
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          box-sizing: border-box;
        }

        .${LEFTBAR_CLASS}-col {
          width: ${COLUMN_WIDTH}px;
          min-width: ${COLUMN_WIDTH}px;
          max-width: ${COLUMN_WIDTH}px;
          border: 1px solid rgba(15,23,42,0.08);
          border-radius: 8px;
          background: transparent;
          padding: 10px 10px 8px;
          box-sizing: border-box;
        }

        .${LEFTBAR_CLASS}-address-select option {
          color: #5f6672;
          background: #ffffff;
        }
      `}</style>

      {isMobile && (
        <div
          style={{
            ...styles.mobileHeader,
            background: `linear-gradient(270deg, ${primaryColor} 37.72%, ${darkenHex(
              primaryColor,
              16
            )} 82.21%)`,
          }}
        >
          <img
            src={MenuIcon}
            alt="Menu"
            style={{ width: "28px", cursor: "pointer" }}
            onClick={() => setIsOpen(true)}
          />
          <p style={styles.titleMobile}>{systemTitle}</p>
        </div>
      )}

      {isMobile && isOpen && (
        <motion.div
          style={{
            position: "fixed",
            top: 56,
            left: 74,
            right: 0,
            background: flyoutBg,
            borderTop: `1px solid ${flyoutBorder}`,
            borderBottom: `1px solid ${flyoutBorder}`,
            borderRight: `1px solid ${flyoutBorder}`,
            padding: `${FLYOUT_PADDING_Y}px ${FLYOUT_PADDING_X}px`,
            zIndex: 1210,
            boxSizing: "border-box",
            overflowX: "auto",
            overflowY: "visible",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {!isMobile && showBackdrop && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}

      {(isOpen || !isMobile) && (
        <AnimatePresence initial={false}>
          <motion.aside
            key="sidebar"
            className={LEFTBAR_CLASS}
            style={{
              ...styles.container,
              background:
                "linear-gradient(180deg, #0D3C46 0%, #142F35 100px, #083640 100%)",
              transform: isMobile
                ? isOpen
                  ? "translateX(0)"
                  : "translateX(-100%)"
                : "translateX(0)",
              zIndex: 1200,
            }}
            initial={isMobile ? "hidden" : false}
            animate="show"
            exit="hidden"
            variants={sidebarVariants}
            onClick={(e) => e.stopPropagation()}
            onMouseLeave={() => {
              if (!isMobile) scheduleCloseFlyout();
            }}
            onMouseEnter={() => {
              if (!isMobile) cancelCloseFlyout();
            }}
          >
            {isMobile && (
              <div style={{ textAlign: "right", padding: "10px 10px 0 10px" }}>
                <motion.button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ✕
                </motion.button>
              </div>
            )}

            <div
              className={`${LEFTBAR_CLASS}-scroll`}
              style={styles.scrollArea}
            >
              <div style={styles.inner}>
                {visibleDepartments.map((dept) => {
                  const deptSelected = isDeptSelected(dept);
                  const active = hoveredDeptName === dept.name || deptSelected;
                  const deptThemeColor = dept.themeColor || "#0a343d";

                  const hoverBackground = toRgba(deptThemeColor, 0.14);
                  const activeBackground = deptThemeColor;

                  return (
                    <div key={dept.name}>
                      <button
                        type="button"
                        className={`${LEFTBAR_CLASS}-rail-button ${
                          active ? "is-active" : ""
                        }`}
                        title={dept.name}
                        onMouseEnter={() => {
                          if (!isMobile) {
                            cancelCloseFlyout();
                            setHoveredDeptName(dept.name);
                          }
                          setHoveredRailDeptName(dept.name);
                        }}
                        onMouseLeave={() => {
                          setHoveredRailDeptName((prev) =>
                            prev === dept.name ? null : prev
                          );
                        }}
                        onClick={() => {
                          if (isMobile) {
                            toggleDepartment(dept);
                          } else {
                            setHoveredDeptName(dept.name);
                            navigate(buildFirstPath(dept));
                          }
                        }}
                        style={{
                          background: deptSelected
                            ? activeBackground
                            : hoveredRailDeptName === dept.name
                            ? hoverBackground
                            : "transparent",
                        }}
                      >
                        <DeptInternetIcon
                          iconKey={dept.iconKey}
                          size={22}
                          color={active ? "#ffffff" : "rgba(255,255,255,0.82)"}
                          alt={dept.name}
                        />
                      </button>

                      {isMobile && (
                        <AnimatePresence initial={false}>
                          {openDepts.includes(dept.name) && (
                            <motion.div
                              initial="collapsed"
                              animate="open"
                              exit="collapsed"
                              variants={mobileDeptVariants}
                              style={{
                                overflow: "hidden",
                                width: 260,
                                marginLeft: 8,
                                marginTop: 0,
                                background: "#F7F8F9",
                                border: "1px solid rgba(15,23,42,0.08)",
                                borderRadius: 14,
                                padding: "10px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 12,
                                  padding: "4px 8px 10px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 15,
                                    fontWeight: 600,
                                    color: dynamicFlyoutTitle,
                                    minWidth: 0,
                                  }}
                                >
                                  {dept.name}
                                </div>

                                {renderAddressSelector(dept)}
                              </div>

                              {dept.items.filter(itemVisible).map((item) => {
                                const mainPath = getMainPath(dept, item);
                                const baseDeptPath = `/${getDeptRouteKey(
                                  dept
                                )}`;
                                const baseMainPath = `${baseDeptPath}/${getMainSlug(
                                  item
                                )}`;
                                const isMainSelected =
                                  location.pathname.startsWith(baseMainPath);

                                return (
                                  <div key={item.label}>
                                    <NavLink
                                      to={mainPath}
                                      onClick={() => {
                                        setIsOpen(false);
                                        closeOverlay();
                                      }}
                                      className={`${LEFTBAR_CLASS}-main-link`}
                                      style={{
                                        borderRadius: 10,
                                        padding: "12px 12px",
                                        marginBottom: 6,
                                        background: isMainSelected
                                          ? toRgba(primaryColor, 0.08)
                                          : "transparent",
                                        color: dynamicFlyoutText,
                                      }}
                                    >
                                      <OptionIcon
                                        label={
                                          norm(item.label) ===
                                          norm("Laboratório - Cases")
                                            ? "circle-outline"
                                            : item.label
                                        }
                                        size={18}
                                      />
                                      {renderLabelWithBadge(
                                        item.label,
                                        item.developed,
                                        item.migrated,
                                        item.markOrange,
                                        item.markBlue
                                      )}
                                    </NavLink>

                                    {renderSubTreeMobile(dept, item)}
                                  </div>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {!isMobile && (
              <AnimatePresence initial={false}>
                {hoveredDept && groupedFlyoutItems.length > 0 && (
                  <motion.div
                    key={hoveredDept.name}
                    variants={flyoutVariants}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    onMouseEnter={cancelCloseFlyout}
                    onMouseLeave={scheduleCloseFlyout}
                    style={{
                      position: "fixed",
                      top: 56,
                      left: 74,
                      right: 0,
                      background: flyoutBg,
                      borderTop: `1px solid ${flyoutBorder}`,
                      borderBottom: `1px solid ${flyoutBorder}`,
                      borderRight: `1px solid ${flyoutBorder}`,
                      padding: `${FLYOUT_PADDING_Y}px ${FLYOUT_PADDING_X}px`,
                      zIndex: 1210,
                      boxSizing: "border-box",
                      overflowX: "auto",
                      overflowY: "visible",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 18,
                          padding: "0 0 10px 5px",
                          marginBottom: 2,
                        }}
                      >
                        <div
                          style={{
                            color: "#545454",
                            fontSize: 14,
                            fontWeight: 400,
                            minWidth: 0,
                          }}
                        >
                          {hoveredDept.name}
                        </div>

                        {renderAddressSelector(hoveredDept)}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: COLUMN_GAP,
                          flexWrap: "nowrap",
                        }}
                      >
                        {groupedFlyoutItems.map((group, groupIndex) => {
                          if (group.type === "main_list") {
                            return (
                              <div
                                key={`main-list-${groupIndex}`}
                                className={`${LEFTBAR_CLASS}-col`}
                                style={{ minHeight: 240 }}
                              >
                                {group.items.map((item) => {
                                  const mainPath = getMainPath(
                                    hoveredDept,
                                    item
                                  );
                                  const baseDeptPath = `/${getDeptRouteKey(
                                    hoveredDept
                                  )}`;
                                  const baseMainPath = `${baseDeptPath}/${getMainSlug(
                                    item
                                  )}`;
                                  const isMainSelected =
                                    location.pathname.startsWith(baseMainPath);

                                  return (
                                    <div
                                      key={item.label}
                                      style={{
                                        marginBottom: 10,
                                        borderRadius: 8,
                                        padding: "8px 8px 6px 8px",
                                        background: isMainSelected
                                          ? "rgba(255,255,255,0.96)"
                                          : "transparent",
                                        border: isMainSelected
                                          ? "1px solid rgba(15,23,42,0.10)"
                                          : "1px solid rgba(15,23,42,0.08)",
                                      }}
                                    >
                                      <NavLink
                                        to={mainPath}
                                        className={`${LEFTBAR_CLASS}-list-button`}
                                        onClick={closeOverlay}
                                        style={{
                                          borderRadius: 8,
                                          padding: "6px 6px",
                                          justifyContent: "space-between",
                                          background: "transparent",
                                          border: "none",
                                          color: dynamicFlyoutText,
                                          marginBottom: item.subitems.length
                                            ? 4
                                            : 0,
                                        }}
                                      >
                                        <span style={{ minWidth: 0 }}>
                                          {renderLabelWithBadge(
                                            item.label,
                                            item.developed,
                                            item.migrated,
                                            item.markOrange,
                                            item.markBlue
                                          )}
                                        </span>

                                        {isMainSelected && (
                                          <span
                                            style={{
                                              width: 3,
                                              height: 18,
                                              borderRadius: 999,
                                              background: toRgba(
                                                menuButtonsSelection,
                                                0.95
                                              ),
                                              flexShrink: 0,
                                            }}
                                          />
                                        )}
                                      </NavLink>

                                      {renderSubTreeDesktop(hoveredDept, item)}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }

                          if (group.type === "single") {
                            const item = group.item;

                            return (
                              <div
                                key={`single-${item.label}`}
                                className={`${LEFTBAR_CLASS}-col`}
                                style={{ minHeight: 240 }}
                              >
                                <NavLink
                                  to={getMainPath(hoveredDept, item)}
                                  className={`${LEFTBAR_CLASS}-main-link`}
                                  onClick={closeOverlay}
                                  style={{
                                    color: dynamicFlyoutText,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    padding: "2px 2px 8px 2px",
                                  }}
                                >
                                  {renderLabelWithBadge(
                                    item.label,
                                    item.developed,
                                    item.migrated,
                                    item.markOrange,
                                    item.markBlue
                                  )}
                                </NavLink>

                                {renderSubTreeDesktop(hoveredDept, item)}
                              </div>
                            );
                          }

                          return (
                            <div
                              key={`standby-group-${groupIndex}`}
                              className={`${LEFTBAR_CLASS}-col`}
                              style={{ minHeight: 240 }}
                            >
                              {group.items.map((item) => (
                                <div
                                  key={item.label}
                                  style={{ marginBottom: 14 }}
                                >
                                  <NavLink
                                    to={getMainPath(hoveredDept, item)}
                                    className={`${LEFTBAR_CLASS}-main-link`}
                                    onClick={closeOverlay}
                                    style={{
                                      color: dynamicFlyoutText,
                                      fontSize: 14,
                                      fontWeight: 600,
                                      padding: "2px 2px 8px 2px",
                                    }}
                                  >
                                    {renderLabelWithBadge(
                                      item.label,
                                      item.developed,
                                      item.migrated,
                                      item.markOrange,
                                      item.markBlue
                                    )}
                                  </NavLink>

                                  {renderSubTreeDesktop(hoveredDept, item)}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </motion.aside>
        </AnimatePresence>
      )}
    </>
  );
};

export default LeftBar;
